import { useDiff } from 'app/state/contexts/DiffContext';
import { memo, useCallback, type ReactNode } from 'react';
import type { ReferenceCell, ReferenceDataValue } from 'entities/document';
import { cellClassNames, cellFormatTag } from 'shared/lib/cell-format';
import { cellStylingClassNames } from 'shared/lib/cell-styling';
import { localizedCellValue } from 'shared/lib/cell-locale';
import ReactDataSheet from 'libs/react-datasheet';

interface DefaultTableProps {
  id?: string;
  data: ReferenceDataValue;
  locale?: string;
  diff?: unknown;
  side?: string;
  showOriginalValue?: boolean;
  cssClass?: string;
}

type TableCell = ReferenceCell & { rawData?: { t?: string } };

export { localizedCellValue };

type DiffCell = [number, ...unknown[]];
type TableDiff = Record<number, Record<number, DiffCell[]>>;

// The props react-datasheet's DataCell hands to a cell renderer. Only the fields
// consumed below are named; everything else (event handlers, style, spans,
// selected, …) is forwarded verbatim onto the rendered <td>/<th> via `...rest`,
// so it is captured by the index signature.
type CellRendererProps = {
  row: number;
  col: number;
  cell: TableCell;
  children?: ReactNode;
  className?: string;
  attributesRenderer?: unknown;
  updated?: boolean;
  editing?: boolean;
  [key: string]: unknown;
};

export const DefaultTable = memo(({ id, data, locale, diff, side, showOriginalValue, cssClass }: DefaultTableProps) => {
  const { diffState } = useDiff();
  const isSelected = diffState?.right[data.superId ?? '']?.selected;

  const valueRenderer = useCallback((cell: TableCell) => localizedCellValue(cell, locale, showOriginalValue), [locale, showOriginalValue]);

  const cellRenderer = useCallback(
    (props: CellRendererProps) => {
      const {
        row,
        col,
        cell,
        children: _children,
        attributesRenderer: _attributesRenderer,
        updated: _updated,
        editing: _editing,
        className: baseClassName,
        ...rest
      } = props;

      const style = {};
      const changed = (diff as TableDiff | undefined)?.[row]?.[col]?.some((d) => d[0] !== 0);
      const className = changed && side === 'right' ? `${baseClassName} ${isSelected ? 'diffed-change-selected' : 'diffed-change-added'}` : baseClassName;

      const css = [cellClassNames(cell), cell.background, cell.border].filter(Boolean).join(' ');

      const stylingClasses = cellStylingClassNames(cell.styling);

      const content = stylingClasses ? <span className={stylingClasses}>{props.children}</span> : props.children;
      if (cellFormatTag(cell.format) === 'th') {
        return (
          <th className={`${className} ${css}`} {...rest} style={style}>
            {content}
          </th>
        );
      }

      return (
        <td className={`${className} ${css}`} {...rest} style={style}>
          {content}
        </td>
      );
    },
    [diff, side, isSelected]
  );

  return (
    <div id={id} contentEditable={false}>
      <ReactDataSheet
        readOnly={true}
        className={`table--reference-data${cssClass ? ' ' + cssClass : ''}`}
        data={data.selectionContent}
        valueRenderer={valueRenderer}
        cellRenderer={cellRenderer}
      />
    </div>
  );
});
